import { homedir } from 'node:os';
import { join } from 'node:path';
import {
  auditSecretLeaks,
  formatSecretFinding,
  type SecretAuditFinding,
} from '../lib/secret-leak-audit.js';

/**
 * ENG-5901 (Phase 1 of ADR-0018) — `agt audit secrets` command.
 *
 * Walks `~/.augmented/<agent>/{provision,project}/.mcp.json` and
 * `~/.augmented/<agent>/.env.integrations` and reports:
 *   - literal (non-templated) secrets in secret-named `.mcp.json`
 *     env/header fields (should be `${VAR}` placeholders), and
 *   - secret files that are group/other-readable (should be 0600).
 *
 * Exit codes:
 *   0 — no findings, OR findings present without `--strict`.
 *   2 — `--strict` and at least one finding (CI deploy gate).
 *
 * Never prints a secret value — only file / agent / server / field.
 */

interface AuditOptions {
  strict?: boolean;
  json?: boolean;
  /** Override the scan root (defaults to ~/.augmented). Used by tests. */
  root?: string;
}

function describe(f: SecretAuditFinding): string {
  if (f.kind === 'literal-secret-in-mcp') {
    return `literal secret in ${f.location} field '${f.field}' of server '${f.server}'`;
  }
  return `secret file is mode ${f.mode} (expected 0600)`;
}

export async function auditSecretsCommand(options: AuditOptions = {}): Promise<void> {
  const root = options.root ?? join(homedir(), '.augmented');
  const findings = auditSecretLeaks(root);

  if (options.json) {
    process.stdout.write(`${JSON.stringify({ findings, root }, null, 2)}\n`);
  } else if (findings.length === 0) {
    process.stdout.write(
      `No literal secrets or world-readable secret files found under ${root}.\n`,
    );
  } else {
    process.stdout.write(
      `Found ${findings.length} secret-handling issue${findings.length === 1 ? '' : 's'} under ${root}:\n\n`,
    );
    for (const f of findings) {
      process.stdout.write(`  • [${f.agent}] ${describe(f)}\n`);
      process.stdout.write(`    ${f.file}\n`);
    }
    process.stdout.write(
      '\nLiteral secrets in .mcp.json should be `${VAR}` placeholders with the raw value in ' +
        '.env.integrations (mode 0600). See docs/operator/credential-migration-eng5898.md.\n',
    );
    for (const f of findings) {
      process.stderr.write(`${formatSecretFinding(f)}\n`);
    }
  }

  if (options.strict && findings.length > 0) {
    process.exit(2);
  }
}
